import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const F = "'DM Sans', system-ui, sans-serif";

function fM(n) { return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fK(n) { return "$" + (Number(n || 0) / 1000).toFixed(1) + "K"; }

export default function RevenueTracker() {
  const { api } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Data from API
  const [summary, setSummary] = useState({ monthly_revenue: 0, yearly_revenue: 0, trend: [], goals: { monthly_goal: 150000, yearly_goal: 1800000 } });
  const [payments, setPayments] = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [billableDays, setBillableDays] = useState({ php_days: 21, iop_days: 17 });
  const [goals, setGoals] = useState({ monthly_goal: 150000, yearly_goal: 1800000 });
  const [targetCensus, setTargetCensus] = useState([]);
  const [projection, setProjection] = useState({ projection_data: [], total: 0 });

  // Projector input state
  const [censusInputs, setCensusInputs] = useState({});

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [summ, pays, sls, bd, gl, tc, proj] = await Promise.all([
        api.get("/revenue/summary"),
        api.get("/revenue/payments"),
        api.get("/revenue/service-lines"),
        api.get("/revenue/billable-days"),
        api.get("/revenue/goals"),
        api.get("/revenue/target-census"),
        api.get("/revenue/projection"),
      ]);
      setSummary(summ);
      setPayments(pays);
      setServiceLines(sls);
      setBillableDays(bd);
      setGoals(gl);
      setTargetCensus(tc);
      setProjection(proj);
      // Initialize census inputs
      const ci = {};
      sls.forEach(sl => { ci[sl.id] = 0; });
      setCensusInputs(ci);
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== PAYMENT HANDLERS =====
  const handleAddPayment = async (date, amount, serviceLine, note) => {
    try {
      const p = await api.post("/revenue/payments", { payment_date: date, amount: parseFloat(amount), service_line: serviceLine, note });
      setPayments(prev => [p, ...prev]);
      const summ = await api.get("/revenue/summary");
      setSummary(summ);
      notify("Payment added");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleDeletePayment = async (id) => {
    try {
      await api.delete("/revenue/payments/" + id);
      setPayments(prev => prev.filter(p => p.id !== id));
      const summ = await api.get("/revenue/summary");
      setSummary(summ);
      notify("Payment deleted");
    } catch (err) { notify("Error: " + err.message); }
  };

  // ===== PROJECTION =====
  const runProjection = async () => {
    const projData = serviceLines.map(sl => {
      const census = censusInputs[sl.id] || 0;
      let revenue = 0;
      if (sl.calculation === "census * billableDays") {
        const days = sl.name.includes("PHP") ? billableDays.php_days : billableDays.iop_days;
        revenue = census * sl.rate * days;
      } else if (sl.calculation === "census * 4 * 4.33") {
        revenue = census * sl.rate * 4 * 4.33;
      } else if (sl.calculation === "census * 1") {
        revenue = census * sl.rate;
      } else if (sl.calculation === "census * programPrice") {
        revenue = census * sl.rate;
      } else {
        revenue = census * sl.rate;
      }
      return { serviceLine: sl.name, census, revenue: Math.round(revenue * 100) / 100 };
    });
    const total = projData.reduce((sum, p) => sum + p.revenue, 0);

    try {
      await api.put("/revenue/projection", {
        projection_data: projData, total: Math.round(total * 100) / 100,
        php_days: billableDays.php_days, iop_days: billableDays.iop_days,
      });
      setProjection({ projection_data: projData, total: Math.round(total * 100) / 100 });
      notify("Projection saved");
    } catch (err) { notify("Error: " + err.message); }
  };

  // ===== SETTINGS HANDLERS =====
  const handleSaveGoals = async (monthly, yearly) => {
    try {
      const g = await api.patch("/revenue/goals", { monthly_goal: parseFloat(monthly), yearly_goal: parseFloat(yearly) });
      setGoals(g);
      notify("Goals saved");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleSaveBillableDays = async (php, iop) => {
    try {
      const bd = await api.patch("/revenue/billable-days", { php_days: parseInt(php), iop_days: parseInt(iop) });
      setBillableDays(bd);
      notify("Billable days saved");
    } catch (err) { notify("Error: " + err.message); }
  };

  // Styles
  const card = { background: "#fff", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" };
  const inp = { width: "100%", padding: "0.5rem 0.75rem", border: "1.5px solid #d1d5db", borderRadius: "6px", fontSize: "0.875rem", fontFamily: F, outline: "none", boxSizing: "border-box" };
  const lab = { display: "block", fontSize: "0.8125rem", fontWeight: "500", color: "#374151", marginBottom: "0.25rem", fontFamily: F };
  const tabBtn = (active) => ({ background: active ? "#0d9488" : "#e2e8f0", color: active ? "#fff" : "#334155", border: "none", borderRadius: "6px", padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontWeight: "600", cursor: "pointer", fontFamily: F });

  if (loading) return <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontFamily: F }}>Loading Revenue Tracker...</div></div>;
  if (error) return <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontFamily: F, textAlign: "center" }}><div>{error}</div><button onClick={loadData} style={tabBtn(true)}>Retry</button></div></div>;

  const monthlyPct = goals.monthly_goal > 0 ? Math.round((summary.monthly_revenue / goals.monthly_goal) * 100) : 0;
  const yearlyPct = goals.yearly_goal > 0 ? Math.round((summary.yearly_revenue / goals.yearly_goal) * 100) : 0;

  return (
    <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "1.25rem" }}>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.375rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
          {["dashboard", "payments", "projector", "lost", "scenarios", "settings"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={tabBtn(tab === t)}>{t === "lost" ? "Lost Revenue" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        {/* DASHBOARD TAB */}
        {tab === "dashboard" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem", marginBottom: "1rem" }}>
              <div style={{ ...card, padding: "1.25rem" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F }}>Monthly Revenue</div>
                <div style={{ fontSize: "1.75rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{fM(summary.monthly_revenue)}</div>
                <div style={{ background: "#e2e8f0", borderRadius: "4px", height: "8px", marginTop: "0.5rem" }}>
                  <div style={{ background: monthlyPct >= 100 ? "#22c55e" : "#0d9488", borderRadius: "4px", height: "8px", width: Math.min(monthlyPct, 100) + "%" }} />
                </div>
                <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F, marginTop: "0.25rem" }}>{monthlyPct}% of {fK(goals.monthly_goal)} goal</div>
              </div>
              <div style={{ ...card, padding: "1.25rem" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F }}>Yearly Revenue</div>
                <div style={{ fontSize: "1.75rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{fM(summary.yearly_revenue)}</div>
                <div style={{ background: "#e2e8f0", borderRadius: "4px", height: "8px", marginTop: "0.5rem" }}>
                  <div style={{ background: yearlyPct >= 100 ? "#22c55e" : "#0d9488", borderRadius: "4px", height: "8px", width: Math.min(yearlyPct, 100) + "%" }} />
                </div>
                <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F, marginTop: "0.25rem" }}>{yearlyPct}% of {fK(goals.yearly_goal)} goal</div>
              </div>
            </div>
            {/* Trend */}
            {summary.trend.length > 0 && (
              <div style={{ ...card, padding: "1.25rem" }}>
                <div style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Monthly Trend</div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", height: "120px" }}>
                  {summary.trend.map((m, i) => {
                    const maxVal = Math.max(...summary.trend.map(t => parseFloat(t.total)));
                    const pct = maxVal > 0 ? (parseFloat(m.total) / maxVal) * 100 : 0;
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                        <div style={{ fontSize: "0.5625rem", fontWeight: "600", fontFamily: F, color: "#0f172a", marginBottom: "0.25rem" }}>{fK(m.total)}</div>
                        <div style={{ width: "100%", maxWidth: "40px", height: Math.max(pct, 5) + "%", background: "#0d9488", borderRadius: "4px 4px 0 0" }} />
                        <div style={{ fontSize: "0.5rem", color: "#64748b", fontFamily: F, marginTop: "0.25rem" }}>{m.month}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {tab === "payments" && <PaymentsTab payments={payments} serviceLines={serviceLines} onAdd={handleAddPayment} onDelete={handleDeletePayment} card={card} inp={inp} lab={lab} />}

        {/* PROJECTOR TAB */}
        {tab === "projector" && (
          <div style={{ ...card, padding: "1.25rem" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Revenue Projector</h3>
            <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>Enter census numbers per service line. PHP uses {billableDays.php_days} billable days/month, IOP uses {billableDays.iop_days}.</div>
            {serviceLines.map(sl => (
              <div key={sl.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{sl.name} ({sl.code || "N/A"})</div>
                  <div style={{ fontSize: "0.625rem", color: "#64748b", fontFamily: F }}>{fM(sl.rate)} {sl.unit}</div>
                </div>
                <input type="number" value={censusInputs[sl.id] || 0} onChange={e => setCensusInputs(p => ({ ...p, [sl.id]: parseInt(e.target.value) || 0 }))} min={0} style={{ ...inp, width: "80px" }} />
              </div>
            ))}
            <button onClick={runProjection} style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: "6px", padding: "0.625rem 1.25rem", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer", fontFamily: F, marginTop: "1rem" }}>Calculate Projection</button>

            {projection.projection_data.length > 0 && (
              <div style={{ marginTop: "1rem", borderTop: "1px solid #e2e8f0", paddingTop: "1rem" }}>
                <div style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.5rem" }}>Projected Monthly Revenue: {fM(projection.total)}</div>
                {projection.projection_data.map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0", fontSize: "0.8125rem", fontFamily: F, borderBottom: "1px solid #f8fafc" }}>
                    <span>{p.serviceLine} (census: {p.census})</span>
                    <span style={{ fontWeight: "600" }}>{fM(p.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* LOST REVENUE TAB */}
        {tab === "lost" && (
          <div style={{ ...card, padding: "1.25rem" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Lost Revenue Analysis</h3>
            <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>Compares projected revenue to target census to show the gap.</div>
            {projection.projection_data.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontFamily: F }}>Run a projection first to see lost revenue analysis.</div>
            ) : (
              <div>
                {projection.projection_data.map((p, i) => {
                  const tc = targetCensus.find(t => t.service_line_name === p.serviceLine);
                  const target = tc ? tc.target_count : 0;
                  const gap = target - p.census;
                  if (gap <= 0) return null;
                  const sl = serviceLines.find(s => s.name === p.serviceLine);
                  const lostRev = gap * (sl ? sl.rate : 0) * (sl && sl.calculation === "census * billableDays" ? (p.serviceLine.includes("PHP") ? billableDays.php_days : billableDays.iop_days) : 1);
                  return (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
                      <div>
                        <div style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{p.serviceLine}</div>
                        <div style={{ fontSize: "0.625rem", color: "#64748b", fontFamily: F }}>Current: {p.census} | Target: {target} | Gap: {gap}</div>
                      </div>
                      <div style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ef4444", fontFamily: F }}>{fM(lostRev)}/mo</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SCENARIOS TAB */}
        {tab === "scenarios" && (
          <div style={{ ...card, padding: "1.25rem" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Scenario Planning</h3>
            <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>How many additional clients are needed to reach the monthly goal of {fM(goals.monthly_goal)}?</div>
            {projection.total >= goals.monthly_goal ? (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "1rem", textAlign: "center", fontFamily: F, color: "#166534" }}>Goal already met with current projection of {fM(projection.total)}</div>
            ) : projection.projection_data.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontFamily: F }}>Run a projection first.</div>
            ) : (
              <div>
                {serviceLines.filter(sl => sl.calculation === "census * billableDays").map(sl => {
                  const gap = goals.monthly_goal - projection.total;
                  const days = sl.name.includes("PHP") ? billableDays.php_days : billableDays.iop_days;
                  const perClient = sl.rate * days;
                  const needed = Math.ceil(gap / perClient);
                  return (
                    <div key={sl.id} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
                      <div>
                        <div style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>+{needed} {sl.name} clients</div>
                        <div style={{ fontSize: "0.625rem", color: "#64748b", fontFamily: F }}>{fM(perClient)}/client/month</div>
                      </div>
                      <div style={{ fontSize: "0.875rem", fontWeight: "700", color: "#0d9488", fontFamily: F }}>= {fM(needed * perClient)}/mo</div>
                    </div>
                  );
                })}
                <div style={{ fontSize: "0.75rem", color: "#ef4444", fontFamily: F, marginTop: "0.75rem" }}>Gap to goal: {fM(goals.monthly_goal - projection.total)}/month</div>
              </div>
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {tab === "settings" && <SettingsTab goals={goals} billableDays={billableDays} serviceLines={serviceLines} onSaveGoals={handleSaveGoals} onSaveBillableDays={handleSaveBillableDays} card={card} inp={inp} lab={lab} />}
      </div>

      {toast && <div style={{ position: "fixed", bottom: "1.25rem", right: "1.25rem", background: "#22c55e", color: "#fff", padding: "0.625rem 1rem", borderRadius: "8px", boxShadow: "0 8px 20px rgba(0,0,0,0.2)", zIndex: 400, fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{toast}</div>}
    </div>
  );
}

// ===== PAYMENTS TAB =====
function PaymentsTab({ payments, serviceLines, onAdd, onDelete, card, inp, lab }) {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState("");
  const [sl, setSl] = useState("");
  const [note, setNote] = useState("");

  const handleAdd = () => {
    if (!date || !amount) return;
    onAdd(date, amount, sl, note);
    setAmount(""); setNote(""); setSl("");
  };

  return (
    <div>
      <div style={{ ...card, padding: "1.25rem", marginBottom: "1rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Add Payment</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0.625rem" }}>
          <div><label style={lab}>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} /></div>
          <div><label style={lab}>Amount ($)</label><input type="number" value={amount} onChange={e => setAmount(e.target.value)} step="0.01" placeholder="0.00" style={inp} /></div>
          <div><label style={lab}>Service Line</label><select value={sl} onChange={e => setSl(e.target.value)} style={inp}><option value="">Optional</option>{serviceLines.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
          <div><label style={lab}>Note</label><input value={note} onChange={e => setNote(e.target.value)} style={inp} /></div>
        </div>
        <button onClick={handleAdd} style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: "600", cursor: "pointer", fontFamily: F, marginTop: "0.75rem" }}>Add Payment</button>
      </div>
      <div style={{ ...card, padding: "1rem" }}>
        <h3 style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.5rem" }}>Payment Log ({payments.length})</h3>
        {payments.length === 0 ? <div style={{ textAlign: "center", padding: "1rem", color: "#94a3b8", fontFamily: F }}>No payments recorded.</div> : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {payments.map(p => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
                <div>
                  <div style={{ fontSize: "0.8125rem", fontFamily: F }}>{p.payment_date} {p.service_line ? "(" + p.service_line + ")" : ""}</div>
                  {p.note && <div style={{ fontSize: "0.625rem", color: "#64748b", fontFamily: F }}>{p.note}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{fM(p.amount)}</span>
                  <button onClick={() => onDelete(p.id)} style={{ background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: "4px", padding: "0.125rem 0.375rem", fontSize: "0.5625rem", cursor: "pointer", fontFamily: F }}>X</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== SETTINGS TAB =====
function SettingsTab({ goals, billableDays, serviceLines, onSaveGoals, onSaveBillableDays, card, inp, lab }) {
  const [mg, setMg] = useState(goals.monthly_goal);
  const [yg, setYg] = useState(goals.yearly_goal);
  const [php, setPhp] = useState(billableDays.php_days);
  const [iop, setIop] = useState(billableDays.iop_days);

  return (
    <div>
      <div style={{ ...card, padding: "1.25rem", marginBottom: "1rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Revenue Goals</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div><label style={lab}>Monthly Goal ($)</label><input type="number" value={mg} onChange={e => setMg(e.target.value)} style={inp} /></div>
          <div><label style={lab}>Yearly Goal ($)</label><input type="number" value={yg} onChange={e => setYg(e.target.value)} style={inp} /></div>
        </div>
        <button onClick={() => onSaveGoals(mg, yg)} style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: "600", cursor: "pointer", fontFamily: F, marginTop: "0.75rem" }}>Save Goals</button>
      </div>
      <div style={{ ...card, padding: "1.25rem", marginBottom: "1rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Billable Days per Month</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div><label style={lab}>PHP (Mon-Fri)</label><input type="number" value={php} onChange={e => setPhp(e.target.value)} style={inp} /></div>
          <div><label style={lab}>IOP (Mon-Thu)</label><input type="number" value={iop} onChange={e => setIop(e.target.value)} style={inp} /></div>
        </div>
        <button onClick={() => onSaveBillableDays(php, iop)} style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: "600", cursor: "pointer", fontFamily: F, marginTop: "0.75rem" }}>Save Billable Days</button>
      </div>
      <div style={{ ...card, padding: "1.25rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Medicaid Rates</h3>
        {serviceLines.map(sl => (
          <div key={sl.id} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
            <div>
              <div style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{sl.name}</div>
              <div style={{ fontSize: "0.625rem", color: "#64748b", fontFamily: F }}>{sl.code || "N/A"} | {sl.unit}</div>
            </div>
            <div style={{ fontSize: "0.875rem", fontWeight: "700", color: "#0d9488", fontFamily: F }}>{fM(sl.rate)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
